import { OptionalPropertyFilters, SearchPropertyStatus } from '../types';
import {
  SEARCH_BOUNDING_BOX_DEFAULT,
  SEARCH_COMMUTES_DEFAULT,
  SEARCH_EXCLUDE_DEPOSIT_TAKEN_DEFAULT,
  SEARCH_EXCLUDE_UNDER_OFFER_DEFAULT,
  SEARCH_FLOW_DEFAULT,
  SEARCH_HAS_IMAGES_DEFAULT,
  SEARCH_KEYWORDS_DEFAULT,
  SEARCH_LOCATION_DEFAULT,
  SEARCH_MAX_PRICE_DEFAULT,
  SEARCH_MIN_BATHROOMS_DEFAULT,
  SEARCH_MIN_BEDROOMS_DEFAULT,
  SEARCH_MIN_CAR_SPACES_DEFAULT,
  SEARCH_MIN_LAND_SIZE_DEFAULT,
  SEARCH_MIN_PRICE_DEFAULT,
  SEARCH_PAGE_NUMBER_DEFAULT,
  SEARCH_POLYGONS_DEFAULT,
  SEARCH_PROPERTY_FEATURES_DEFAULT_ITEM,
  SEARCH_PROPERTY_STATUS_DEFAULT,
  SEARCH_PROPERTY_TYPE_DEFAULT_ITEM,
  SEARCH_SEPARATOR,
  SEARCH_SURROUNDING_SUBURBS_DEFAULT,
  SEARCH_VIEW_DEFAULT,
} from './defaults';
import { getDefaultSortOption } from './getDefaultSortOption';
import { BudgetFilters } from './queryStringHelpers/getBudgetFiltersFromUrl';

export const isLocationNotDefault = (locations: string): boolean =>
  locations !== SEARCH_LOCATION_DEFAULT;

export const isBoundingBoxNotDefault = (boundingBox: string): boolean =>
  boundingBox !== SEARCH_BOUNDING_BOX_DEFAULT;

export const isPolygonsNotDefault = (polygons: string): boolean =>
  polygons !== SEARCH_POLYGONS_DEFAULT;

export const isSurroundingSuburbsNotDefault = (surroundingSuburbs: string): boolean =>
  surroundingSuburbs !== SEARCH_SURROUNDING_SUBURBS_DEFAULT;

export const isPropertyStatusNotDefault = (propertyStatus: string): boolean =>
  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
  propertyStatus !== SEARCH_PROPERTY_STATUS_DEFAULT;

export const isPropertyTypeNotDefault = (propertyType: string): boolean =>
  propertyType !== SEARCH_PROPERTY_TYPE_DEFAULT_ITEM;

export const isMinBedroomsNotDefault = (bedrooms: string): boolean =>
  bedrooms !== SEARCH_MIN_BEDROOMS_DEFAULT;

export const isMinBathroomsNotDefault = (bathrooms: string): boolean =>
  bathrooms !== SEARCH_MIN_BATHROOMS_DEFAULT;

export const isMinLandsizeNotDefault = (landSize: string): boolean =>
  landSize !== SEARCH_MIN_LAND_SIZE_DEFAULT;

export const isIndoorFeaturesNotDefault = (indoorFeatures: string): boolean =>
  indoorFeatures !== SEARCH_PROPERTY_FEATURES_DEFAULT_ITEM;

export const isOutdoorFeaturesNotDefault = (outdoorFeatures: string): boolean =>
  outdoorFeatures !== SEARCH_PROPERTY_FEATURES_DEFAULT_ITEM;

export const isEcoFriendlyFeaturesNotDefault = (ecoFriendlyFeatures: string): boolean =>
  ecoFriendlyFeatures !== SEARCH_PROPERTY_FEATURES_DEFAULT_ITEM;

export const isSearchViewNotDefault = (view: string): boolean => view !== SEARCH_VIEW_DEFAULT;

export const isSearchFlowNotDefault = (flow: string): boolean => flow !== SEARCH_FLOW_DEFAULT;

export const isSearchCommutesNotDefault = (commutes: string): boolean =>
  commutes !== SEARCH_COMMUTES_DEFAULT;

export const isMinCarSpacesNotDefault = (minCarSpaces: string): boolean =>
  minCarSpaces !== SEARCH_MIN_CAR_SPACES_DEFAULT;

export const isMinPriceNotDefault = (minPrice: string): boolean =>
  minPrice !== SEARCH_MIN_PRICE_DEFAULT;

export const isMaxPriceNotDefault = (maxPrice: string): boolean =>
  maxPrice !== SEARCH_MAX_PRICE_DEFAULT;

export const isKeywordsNotDefault = (keywords: string): boolean =>
  keywords !== SEARCH_KEYWORDS_DEFAULT;
export const isPageNumberNotDefault = (pageNumber: string): boolean =>
  pageNumber !== SEARCH_PAGE_NUMBER_DEFAULT;

export const isExcludeUnderOfferNotDefault = (excludeUnderOffer: string): boolean =>
  excludeUnderOffer !== SEARCH_EXCLUDE_UNDER_OFFER_DEFAULT;

export const isExcludeDepositTakenNotDefault = (excludeDepositTaken: string): boolean =>
  excludeDepositTaken !== SEARCH_EXCLUDE_DEPOSIT_TAKEN_DEFAULT;

export const isHasImagesNotDefault = (hasImages: string): boolean =>
  hasImages !== SEARCH_HAS_IMAGES_DEFAULT;

export const isSortOptionNotDefault = (
  sortOption: string,
  propertyStatus: SearchPropertyStatus,
  isGeoShapeBasedSearch: boolean = false,
): boolean => {
  const { defaultValue } = getDefaultSortOption({
    isGeoShapeBasedSearch,
    propertyStatus,
  });
  return sortOption !== defaultValue;
};

export const countActiveLocationFilters = (locations: string): number => {
  return locations.split(SEARCH_SEPARATOR).length;
};

export const countActiveBudgetFilters = ({ minPrice, maxPrice }: BudgetFilters): number => {
  return isMinPriceNotDefault(minPrice) || isMaxPriceNotDefault(maxPrice) ? 1 : 0;
};

export const countActivePropertyFilters = ({
  propertyStatus,
  optionalPropertyFilters,
}: {
  propertyStatus: SearchPropertyStatus;
  optionalPropertyFilters: OptionalPropertyFilters;
}): number => {
  let activeFilterCount = 1;

  const {
    propertyTypes,
    minBedrooms,
    minBathrooms,
    minLandSize,
    indoorFeatures,
    outdoorFeatures,
    ecoFriendlyFeatures,
    minCarSpaces,
    keywords,
  } = optionalPropertyFilters;

  if (propertyStatus !== SearchPropertyStatus.offMarket && isPropertyTypeNotDefault(propertyTypes))
    activeFilterCount += 1;
  if (isMinBedroomsNotDefault(minBedrooms)) activeFilterCount += 1;
  if (isMinBathroomsNotDefault(minBathrooms)) activeFilterCount += 1;
  if (isMinLandsizeNotDefault(minLandSize)) activeFilterCount += 1;
  if (isIndoorFeaturesNotDefault(indoorFeatures)) activeFilterCount += 1;
  if (isOutdoorFeaturesNotDefault(outdoorFeatures)) activeFilterCount += 1;
  if (isEcoFriendlyFeaturesNotDefault(ecoFriendlyFeatures)) activeFilterCount += 1;
  if (isMinCarSpacesNotDefault(minCarSpaces)) activeFilterCount += 1;
  if (isKeywordsNotDefault(keywords)) activeFilterCount += 1;

  return activeFilterCount;
};
